import React, { useRef } from "react";
import {
    Box,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    DialogContent,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadForOfflineRoundedIcon from "@mui/icons-material/DownloadForOfflineRounded";
import HeaderImg from "../assets/Top_01.png";
import FooterImg from "../assets/Bottom_01.png";
import xpay from "../assets/xpay.png";
import { useTranslation } from "react-i18next";

// ── types ─────────────────────────────────────────────────────────────────────
export interface InstallmentDetail {
    loanAccountNo: string;
    installmentNo: string;
    dueDate: string;
    installmentAmount: string;
}

export interface TransactionReceipt {
    civilId: string;
    status: string;
    message: string;
    custName: string;
    custAddress: string;
    custMobile: string;
    receiptDate: string;
    receiptNo: string;
    chargesAmount: string;
    subTotal: string;
    totalAmount: string;
    ottuTranId: string;
    InstallmentDetails: InstallmentDetail[];
}


const fmt = (n: number) =>
    n.toLocaleString("en-KW", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + " KD";

// ── RTL-aware arrow ───────────────────────────────────────────────────────────
const PlayArrow = ({ isRTL }: { isRTL?: boolean }) => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ transform: isRTL ? "scaleX(-1)" : "none", flexShrink: 0 }}
    >
        <path
            d="M2 1.5L11 6.5L2 11.5V1.5Z"
            stroke="#2493C7"
            strokeWidth="1.5"
            strokeLinejoin="round"
            fill="none"
        />
    </svg>
);

// ── left-side circle half decoration (swaps side in RTL) ─────────────────────
const LeftCircleDecor = ({ isRTL }: { isRTL?: boolean }) => (
    <Box
        sx={{
            position: "absolute",
            ...(isRTL ? { right: -68 } : { left: -68 }),
            top: "30%",
            transform: "translateY(-50%)",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "#DAF2FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <Box sx={{ width: 60, height: 60, borderRadius: "50%", background: "white" }} />
    </Box>
);

// ── right-side circle half decoration (swaps side in RTL) ────────────────────
const RightCircleDecor = ({ isRTL }: { isRTL?: boolean }) => (
    <Box
        sx={{
            position: "absolute",
            ...(isRTL ? { left: -70 } : { right: -70 }),
            bottom: 40,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "#DAF2FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 0,
        }}
    >
        <Box sx={{ width: 60, height: 60, borderRadius: "50%", background: "white" }} />
    </Box>
);

// ── diagonal watermark lines (swaps side in RTL) ──────────────────────────────
const DiagonalLines = ({ isRTL }: { isRTL?: boolean }) => (
    <Box sx={{ position: "absolute", ...(isRTL ? { left: 50 } : { right: 50 }), top: 50, opacity: 0.25 }}>
        <svg width="90" height="30" viewBox="0 0 90 55">
            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90].map((offset, i) => (
                <line
                    key={i}
                    x1={offset + 35}
                    y1="0"
                    x2={offset}
                    y2="55"
                    stroke="#DAF2FF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            ))}
        </svg>
    </Box>
);

// ── main component ────────────────────────────────────────────────────────────
interface Props {
    transaction: TransactionReceipt | null;
    onClose?: () => void;
}

const TransactionReceiptModel: React.FC<Props> = ({
    transaction,
    onClose = () => {},
}) => {
    const contentRef = useRef<HTMLDivElement>(null);

    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar";

    if (!transaction) return null;

    const tx = transaction;

    const handleDownload = () => {
        if (!contentRef.current) return;
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const html2pdf = require("html2pdf.js");
        const opt = {
            margin: 0,
            filename: `receipt-${tx.receiptNo}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        };
        html2pdf().set(opt).from(contentRef.current).save();
    };

    const subTotal = Number(tx.subTotal);
    const charges  = Number(tx.chargesAmount);
    const total    = Number(tx.totalAmount);

    return (
        <>
            {/* ── action bar ── */}
            <Grid
                container
                justifyContent={isRTL ? "flex-start" : "flex-end"}
                alignItems="center"
                direction={isRTL ? "row-reverse" : "row"}
                sx={{ pr: isRTL ? 0 : 1, pl: isRTL ? 1 : 0, pt: 0.5 }}
            >
                <IconButton onClick={handleDownload} sx={{ color: "#2493C7" }}>
                    <DownloadForOfflineRoundedIcon />
                </IconButton>
                <IconButton onClick={onClose} sx={{ color: "#555" }}>
                    <CancelIcon />
                </IconButton>
            </Grid>

            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box
                    ref={contentRef}
                    dir={isRTL ? "rtl" : "ltr"}
                    sx={{
                        width: "100%",
                        maxWidth: "550px",
                        mx: "auto",
                        background: "#fff",
                        boxShadow: "0 2px 20px rgba(0,0,0,0.10)",
                        position: "relative",
                        overflow: "hidden",
                        direction: isRTL ? "rtl" : "ltr",
                    }}
                >
                    {/* ══ HEADER IMAGE ══ */}
                    <Box sx={{ position: "relative", lineHeight: 0 }}>
                        {/* scaleX(-1) mirrors the header so teal/dark side flips for RTL */}
                        <img
                            src={HeaderImg}
                            alt="header"
                            style={{
                                width: "100%",
                                height: "120px",
                                objectFit: "cover",
                                display: "block",
                                transform: isRTL ? "scaleX(-1)" : "none",
                            }}
                        />
                        {/* logo — right side in LTR, left side in RTL (follows the image flip) */}
                        <Box
                            sx={{
                                position: "absolute",
                                ...(isRTL ? { left: 24 } : { right: 24 }),
                                top: "50%",
                                transform: "translateY(-50%)",
                            }}
                        >
                            <img src={xpay} alt="Logo" style={{ width: "88px" }} />
                        </Box>
                        <Box
                            sx={{
                                position: "absolute",
                                ...(isRTL ? { left: 24 } : { right: 24 }),
                                top: "calc(50% + 60px)",
                                textAlign: isRTL ? "left" : "right",
                            }}
                        >
                            <Typography sx={{ fontSize: "10px", color: "#090000" }}>
                                <Box component="span" sx={{ fontWeight: 700 }}>Merchant :</Box>{" "}
                                Xcite General Trading Company S.P.C
                            </Typography>
                        </Box>
                    </Box>

                    {/* ══ BODY ══ */}
                    <Box sx={{ px: 3, pb: 0, position: "relative" }}>
                        <LeftCircleDecor isRTL={isRTL} />

                        {/* ▷ / ◁ arrow row */}
                        <Box sx={{ display: "flex", gap: "5px", pt: 2, pb: 1.5, flexWrap: "wrap" }}>
                            {Array.from({ length: 9 }).map((_, i) => (
                                <PlayArrow key={i} isRTL={isRTL} />
                            ))}
                        </Box>

                        {/*
                          direction:rtl (inherited) reverses the flex row automatically:
                          Customer (DOM first) → RIGHT ✓, Invoice box (DOM second) → LEFT ✓
                          Do NOT use flexDirection:row-reverse — double-reversal brings it back to LTR.
                        */}
                        {/* ── customer info + invoice box ── */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                flexDirection: "row",
                                mb: 2,
                                position: "relative",
                                gap: 2,
                            }}
                        >
                            {/* customer info */}
                            <Box sx={{ textAlign: isRTL ? "right" : "left" }}>
                                {/* <Typography sx={{ fontSize: 11, color: "#444", mb: 0.3 }}>
                                    {t("transactionreceipt.invoiceto")}:
                                </Typography> */}
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111" }}>
                                    {tx.custName}
                                </Typography>
                                {tx.custAddress.split("\n").map((line, i) => (
                                    <Typography key={i} sx={{ fontSize: 11, color: "#444", lineHeight: 1.6 }}>
                                        {line}
                                    </Typography>
                                ))}
                                <Typography sx={{ fontSize: 11, color: "#444" }}>{tx.custMobile}</Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2493C7", mt: 0.5 }}>
                                    {t("transactionreceipt.civilId")}:- {tx.civilId}
                                </Typography>
                            </Box>

                            {/* invoice number + date box */}
                            <Box sx={{ position: "relative", flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        background: "#DAF2FF",
                                        borderRadius: "20px",
                                        px: 2,
                                        py: 1,
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 2,
                                        alignItems: "center",
                                        minWidth: 180,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#111", whiteSpace: "nowrap" }}>
                                        {t("transactionreceipt.invoiceno")} : {tx.receiptNo}
                                    </Typography>
                                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#111", whiteSpace: "nowrap" }}>
                                        {t("transactionreceipt.date")} : {tx.receiptDate}
                                    </Typography>
                                </Box>
                                <DiagonalLines isRTL={isRTL} />
                            </Box>
                        </Box>

                        {/* ── installments table ── */}
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                mt: 0.5,
                                mb: 0,
                                borderRadius: "5px",
                                overflowX: "auto",
                                overflowY: "hidden",
                                border: "none",
                                "&::-webkit-scrollbar": { height: "6px" },
                                "&::-webkit-scrollbar-thumb": { background: "#bdbdbd", borderRadius: "10px" },
                            }}
                        >
                            <Table size="small" sx={{ minWidth: 500 }}>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            background: "#2493C7",
                                            "& td, & th": { border: 0, color: "#fff" },
                                            "& td:first-of-type, & th:first-of-type": {
                                                borderTopLeftRadius: isRTL ? 0 : "25px",
                                                borderBottomLeftRadius: isRTL ? 0 : "25px",
                                                borderTopRightRadius: isRTL ? "25px" : 0,
                                                borderBottomRightRadius: isRTL ? "25px" : 0,
                                            },
                                            "& td:last-of-type, & th:last-of-type": {
                                                borderTopRightRadius: isRTL ? 0 : "25px",
                                                borderBottomRightRadius: isRTL ? 0 : "25px",
                                                borderTopLeftRadius: isRTL ? "25px" : 0,
                                                borderBottomLeftRadius: isRTL ? "25px" : 0,
                                            },
                                        }}
                                    >
                                        {[
                                            t("transactionreceipt.paymentno"),
                                            t("transactionreceipt.account"),
                                            t("transactionreceipt.paymentAmount"),
                                            t("transactionreceipt.totalAmount"),
                                        ].map((h) => (
                                            <TableCell
                                                key={h}
                                                align="center"
                                                sx={{
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    py: 1.4,
                                                    border: "none",
                                                    background: "#2493C7",
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tx.InstallmentDetails.map((row, i) => (
                                        <TableRow
                                            key={i}
                                            sx={{
                                                "& td": { borderBottom: "1px solid #e8e8e8" },
                                                "&:last-child td": { borderBottom: "none" },
                                            }}
                                        >
                                            <TableCell align="center" sx={{ fontSize: 11, py: 1.3 }}>{row.installmentNo}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 11, py: 1.3 }}>{row.loanAccountNo ?? "-"}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 11, py: 1.3 }}>{fmt(Number(row.installmentAmount))}</TableCell>
                                            <TableCell align="center" sx={{ fontSize: 11, py: 1.3 }}>{fmt(Number(row.installmentAmount))}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* ── totals + signature ── */}
                        {/*
                          direction:rtl (inherited) already reverses the flex row:
                          order=1 → RIGHT, order=2 → LEFT in RTL.
                          So no order-swapping needed — the natural DOM order works.
                        */}
                        <Grid container sx={{ mt: 1, mb: 1.5 }}>
                            {/* signature — LEFT in LTR (order 1 = leftmost), RIGHT in RTL (order 1 = rightmost) */}
                            <Grid item xs={7} sx={{ order: 1 }}>
                                {/* <Box sx={{ mt: 6, textAlign: isRTL ? "right" : "left" }}>
                                    <Box
                                        sx={{
                                            borderBottom: "1.5px solid #555",
                                            width: 120,
                                            mb: 0.5,
                                            ml: isRTL ? "auto" : 0,
                                            mr: isRTL ? 0 : "auto",
                                        }}
                                    />
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#111" }}>
                                        {t("transactionreceipt.authorSignature")}
                                    </Typography>
                                </Box> */}
                            </Grid>

                            <RightCircleDecor isRTL={isRTL} />

                            {/* totals — RIGHT in LTR (order 2), LEFT in RTL (order 2 = leftmost) */}
                            <Grid
                                item
                                xs={5}
                                sx={{ position: "relative", zIndex: 2, order: 2 }}
                            >
                                <Table size="small">
                                    <TableBody>
                                        {[
                                            { label: t("transactionreceipt.subTotal") + ":", value: fmt(subTotal), bold: false },
                                            { label: t("transactionreceipt.charges") + ":", value: charges === 0 ? "0.0" : fmt(charges), bold: false },
                                            { label: t("transactionreceipt.total") + ":", value: fmt(total), bold: true },
                                        ].map(({ label, value, bold }) => (
                                            <TableRow key={label} sx={{ "& td": { borderBottom: "none", py: 0.4 } }}>
                                                <TableCell
                                                    align={isRTL ? "right" : "left"}
                                                    sx={{
                                                        fontSize: 11,
                                                        color: "#555",
                                                        fontWeight: bold ? 700 : 400,
                                                        pl: isRTL ? 0 : 0,
                                                        pr: isRTL ? 0 : undefined,
                                                    }}
                                                >
                                                    {label}
                                                </TableCell>
                                                <TableCell
                                                    align={isRTL ? "left" : "right"}
                                                    sx={{
                                                        fontSize: 11,
                                                        fontWeight: bold ? 700 : 500,
                                                        color: "#111",
                                                        padding: "25px",
                                                    }}
                                                >
                                                    {value}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* ══ FOOTER IMAGE ══ */}
                    <Box sx={{ position: "relative", lineHeight: 0 }}>
                        {/* scaleX(-1) mirrors the image so teal moves to left in RTL */}
                        <img
                            src={FooterImg}
                            alt="footer"
                            style={{
                                width: "100%",
                                height: "120px",
                                objectFit: "cover",
                                display: "block",
                                transform: isRTL ? "scaleX(-1)" : "none",
                            }}
                        />
                        {/*
                          direction:rtl (inherited) reverses the flex row automatically:
                          Terms (DOM first) → RIGHT (light bg after image flip) ✓
                          Contact (DOM second) → LEFT (teal bg after image flip) ✓
                          Do NOT use flexDirection:row-reverse here — it would double-reverse
                          back to LTR, making white contact text land on the light section (invisible).
                        */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                px: 3,
                            }}
                        >
                            {/* terms — light side (left in LTR, right in RTL via direction:rtl) */}
                            {/* <Box sx={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#121111", mb: 0.3 }}>
                                    {t("transactionreceipt.termsConditions")}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: "rgba(11,11,11,0.85)", maxWidth: 170, lineHeight: 1.5 }}>
                                    Lorem ipsum dolor sit amet, consectetuer adipiscing elit.
                                </Typography>
                            </Box> */}

                            {/* contact — teal side (right in LTR, left in RTL via direction:rtl) */}
                            {/* <Box sx={{ textAlign: isRTL ? "left" : "right", minWidth: 160 }}>
                                <Typography sx={{ fontSize: 10, color: "#fff", fontWeight: 500 }}>Your Address Here</Typography>
                                <Typography sx={{ fontSize: 10, color: "#fff" }}>+965 2444876</Typography>
                                <Typography sx={{ fontSize: 10, color: "#fff" }}>www.yourwebsite.com</Typography>
                            </Box> */}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </>
    );
};

export default TransactionReceiptModel;
